import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { reportsApi } from "./api";
import { SlaBadge } from "./SlaBadge";
import type { Report, ReportStatus } from "./types";

const STATUSES: ReportStatus[] = [
  "submitted",
  "acknowledged",
  "in_progress",
  "resolved",
  "duplicate",
  "dismissed",
];

const TONE: Record<ReportStatus, string> = {
  submitted: "bg-blue-100 text-blue-800",
  triaged: "bg-cyan-100 text-cyan-800",
  acknowledged: "bg-amber-100 text-amber-800",
  in_progress: "bg-orange-100 text-orange-800",
  resolved: "bg-emerald-100 text-emerald-800",
  duplicate: "bg-slate-100 text-slate-700",
  dismissed: "bg-slate-100 text-slate-500",
};

export function ReportsListPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const facilityId = Number(id);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | "all">("all");

  const { data, isLoading } = useQuery({
    queryKey: ["reports", facilityId, statusFilter],
    queryFn: () =>
      reportsApi.list({
        facility: facilityId,
        status: statusFilter === "all" ? undefined : statusFilter,
      }),
    enabled: !Number.isNaN(facilityId),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("reports.title")}</h1>
        <Link
          to={`/facilities/${facilityId}`}
          className="text-sm text-brand-600 hover:underline"
        >
          {t("reports.back_facility")}
        </Link>
      </header>

      <div className="flex flex-wrap gap-2">
        <Chip
          label={t("reports.filter_all")}
          active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        />
        {STATUSES.map((s) => (
          <Chip
            key={s}
            label={t(`reports.status.${s}` as const)}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          />
        ))}
      </div>

      {isLoading ? (
        <p className="text-slate-500">{t("common.loading")}</p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
          {data?.results.map((r) => <Row key={r.id} r={r} facilityId={facilityId} />)}
          {data && data.results.length === 0 && (
            <li className="p-4 text-slate-500">{t("reports.no_match")}</li>
          )}
        </ul>
      )}
    </div>
  );
}

function Row({ r, facilityId }: { r: Report; facilityId: number }) {
  const { t } = useTranslation();
  return (
    <li className="hover:bg-slate-50">
      <Link
        to={`/facilities/${facilityId}/reports/${r.id}`}
        className="flex items-center justify-between p-4"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{r.title || t("reports.no_title")}</p>
          <p className="truncate text-sm text-slate-500">
            {r.body || "—"}
          </p>
        </div>
        <div className="ml-3 flex flex-col items-end gap-1">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${TONE[r.status]}`}
          >
            {t(`reports.status.${r.status}` as const)}
          </span>
          <SlaBadge breachAt={r.sla_breach_at} />
        </div>
      </Link>
    </li>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
        (active
          ? "bg-slate-900 text-white"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200")
      }
    >
      {label}
    </button>
  );
}
